import React, { useState } from 'react';

const ShippingAddressSelector = ({ 
  shippingAddresses, 
  selectedAddressId, 
  onAddressSelect, 
  currentShipAddress,
  disabled = false 
}) => {
  const [showAddresses, setShowAddresses] = useState(false);
  const [showAllAddresses, setShowAllAddresses] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const formatAddressForCard = (address) => {
    // Handle both string and object formats
    if (typeof address === 'string') {
      return address.replace(/\n/g, ', ');
    }
    
    if (typeof address === 'object' && address !== null) {
      const parts = [
        address.address_1,
        address.address_2,
        address.city,
        address.zip_code,
        address.country
      ].filter(part => part && part.trim() !== '');
      return parts.join(', ');
    }
    
    return String(address || '');
  };

  const filteredAddresses = searchQuery.trim()
    ? (shippingAddresses || []).filter(a =>
        formatAddressForCard(a.address).toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(a.ship_internal_id).includes(searchQuery)
      )
    : shippingAddresses || [];

  const displayAddresses = showAllAddresses
    ? filteredAddresses
    : filteredAddresses.slice(0, 3);

  return (
    <div className="shipping-address-selector">
      {/* Clickable Header */}
      <div 
        className={`d-flex align-items-center justify-content-between p-2 border rounded ${
          disabled ? 'bg-light text-muted' : 'bg-white'
        }`}
        style={{ 
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s ease'
        }}
        onClick={() => { if (!disabled) { setShowAddresses(!showAddresses); setSearchQuery(''); } }}
      >
        <div className="d-flex align-items-center">
          <i className="bi bi-geo-alt text-primary me-2"></i>
          <span className="form-label fw-semibold mb-0">เลือกที่อยู่จัดส่ง</span>
          {selectedAddressId && (
            <span className="badge bg-success ms-2">เลือกแล้ว</span>
          )}
        </div>
        <div className="d-flex align-items-center">
          {shippingAddresses && shippingAddresses.length > 0 && (
            <span className="badge bg-primary me-2">{shippingAddresses.length} ตัวเลือก</span>
          )}
          <i className={`bi ${showAddresses ? 'bi-chevron-up' : 'bi-chevron-down'} text-primary`}></i>
        </div>
      </div>

      {/* Current Address Display - Always Visible */}
      {currentShipAddress && (
        <div className="mt-2">
          <div className="bg-light rounded p-2 border-start border-primary border-3">
            <div className="d-flex align-items-center">
              <i className="bi bi-geo-alt-fill text-primary me-2"></i>
              <span className="badge bg-primary me-2">ปัจจุบัน</span>
              <small className="text-dark">{formatAddressForCard(currentShipAddress)}</small>
            </div>
          </div>
        </div>
      )}

      {/* Collapsible Address Options */}
      {showAddresses && (
        <div className="mt-2 border rounded p-2 bg-light">
          {shippingAddresses && shippingAddresses.length > 0 ? (
            <>
              {/* Search */}
              <div className="mb-2">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="ค้นหาที่อยู่..."
                  value={searchQuery}
                  onChange={e => { setSearchQuery(e.target.value); setShowAllAddresses(true); }}
                  onClick={e => e.stopPropagation()}
                  autoFocus
                />
              </div>
              <div className="d-grid gap-2">
                {displayAddresses.map((addressOption) => {
                  const isSelected = addressOption.ship_internal_id === selectedAddressId;
                  const isCurrent = addressOption.ship_internal_id === selectedAddressId;
                  
                  return (
                    <div 
                      key={addressOption.ship_internal_id}
                      className={`border rounded p-2 ${
                        isSelected ? 'border-primary bg-primary bg-opacity-10' : 'border-light bg-white'
                      } ${disabled ? 'opacity-50' : ''}`}
                      style={{ 
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onClick={() => {
                        if (!disabled) {
                          onAddressSelect(addressOption.ship_internal_id);
                          setShowAddresses(false); // Auto-close after selection
                        }
                      }}
                    >
                      <div className="d-flex align-items-center">
                        {/* Selection Indicator */}
                        <div className={`rounded-circle d-flex align-items-center justify-content-center me-2 ${
                          isSelected ? 'bg-primary' : 'bg-light'
                        }`} style={{ width: '20px', height: '20px', minWidth: '20px' }}>
                          {isSelected ? (
                            <i className="bi bi-check text-white" style={{ fontSize: '10px' }}></i>
                          ) : (
                            <i className="bi bi-circle text-muted" style={{ fontSize: '10px' }}></i>
                          )}
                        </div>
                        
                        {/* Address Content */}
                        <div className="flex-grow-1 me-2">
                          <div className="d-flex align-items-center flex-wrap gap-1">
                            <span className={`badge badge-sm ${isCurrent ? 'bg-success' : 'bg-secondary'}`} 
                                  style={{ fontSize: '0.7rem' }}>
                              {isCurrent ? 'ปัจจุบัน' : `#${addressOption.ship_internal_id}`}
                            </span>
                            {isSelected && !isCurrent && (
                              <span className="badge bg-primary badge-sm" style={{ fontSize: '0.7rem' }}>เลือกแล้ว</span>
                            )}
                          </div>
                          <div className="text-dark small mt-1" style={{ lineHeight: '1.3' }}>
                            {formatAddressForCard(addressOption.address)}
                          </div>
                        </div>
                        
                        {/* Status Icon */}
                        <div>
                          {isSelected ? (
                            <i className="bi bi-check-circle-fill text-primary"></i>
                          ) : (
                            <i className="bi bi-circle text-muted"></i>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Show More/Less Button - Compact */}
              {filteredAddresses.length === 0 && (
                <div className="text-muted small text-center py-2">ไม่พบที่อยู่ที่ตรงกัน</div>
              )}
              {filteredAddresses.length > 3 && (
                <div className="text-center mt-2">
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm px-3 py-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowAllAddresses(!showAllAddresses);
                    }}
                    disabled={disabled}
                    style={{ fontSize: '0.8rem' }}
                  >
                    {showAllAddresses ? (
                      <>
                        <i className="bi bi-chevron-up me-1"></i>
                        แสดงน้อยลง
                      </>
                    ) : (
                      <>
                        <i className="bi bi-chevron-down me-1"></i>
                        แสดงทั้งหมด ({filteredAddresses.length})
                      </>
                    )}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-3">
              <i className="bi bi-geo-alt text-muted mb-2" style={{ fontSize: '1.5rem' }}></i>
              <div className="text-muted small">ไม่มีที่อยู่จัดส่งเพิ่มเติมสำหรับลูกค้านี้</div>
            </div>
          )}
        </div>
      )}

      {/* Selection Status - Only show when collapsed and has selection */}
      {!showAddresses && selectedAddressId && selectedAddressId !== currentShipAddress && (
        <div className="mt-2">
          <div className="bg-success bg-opacity-10 border border-success rounded p-2 d-flex align-items-center">
            <i className="bi bi-check-circle-fill text-success me-2"></i>
            <small className="text-success mb-0">จะเปลี่ยนที่อยู่จัดส่งเมื่อบันทึก</small>
          </div>
        </div>
      )}

      {disabled && (
        <div className="mt-2">
          <small className="text-danger d-flex align-items-center">
            <i className="bi bi-lock me-1"></i>
            ไม่สามารถเปลี่ยนที่อยู่จัดส่งได้เนื่องจากคำสั่งซื้อถูกล็อค
          </small>
        </div>
      )}
    </div>
  );

};

// Integration code for your EditOrder component:
/*
Replace the shipping address dropdown section (around line 580-630) with:

<div className="col-12">
  <ShippingAddressSelector
    shippingAddresses={shippingAddresses}
    selectedAddressId={formData.shipaddresslist}
    onAddressSelect={(addressId) => {
      if (orderLocked) {
        toast.error('ไม่สามารถแก้ไขได้ เนื่องจากสถานะคำสั่งซื้อเป็น: ' + orderStatus);
        return;
      }
      setFormData(prev => ({
        ...prev,
        shipaddresslist: addressId
      }));
    }}
    currentShipAddress={formData.currentShipAddress}
    disabled={orderLocked}
  />
</div>

And add this import at the top:
import ShippingAddressSelector from './ShippingAddressSelector'; // adjust path as needed
*/

export default ShippingAddressSelector;